/**
 * Welke handleiding de Help (?-knop) toont, per UI-taal.
 *
 * - Nederlands: `docs/user-guide.md`, één bestand.
 * - Engels: `docs/user-guide/`, de index (`README.md`) en de hoofdstukken
 *   `01-…md` t/m `19-…md`, in die volgorde onder elkaar.
 *
 * Beide worden bij het bouwen in de bundel opgenomen (`?raw`), net als
 * voorheen de Nederlandse: geen fetch, altijd de versie van de build.
 * Afbeeldingen (`docs/user-guide/images/`) gaan als URL mee voor zover ze
 * bestaan; een verwijzing naar een afbeelding die er (nog) niet is, toont
 * niets in plaats van een kapot plaatje.
 */
import nlGuide from "../../../../docs/user-guide.md?raw";

const enFiles = import.meta.glob("../../../../docs/user-guide/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

const enImages = import.meta.glob("../../../../docs/user-guide/images/*", {
  query: "?url",
  import: "default",
  eager: true,
}) as Record<string, string>;

export interface GuideChapter {
  /** Bestandsnaam, bv. `04-strategy-and-business-model.md`. */
  file: string;
  /** Element-id waar de Help naartoe scrolt. */
  anchor: string;
  markdown: string;
}

function baseName(path: string): string {
  return path.slice(path.lastIndexOf("/") + 1);
}

export function chapterAnchor(file: string): string {
  return `guide-${file.replace(/\.md$/, "").toLowerCase()}`;
}

/** De Engelse hoofdstukken in leesvolgorde: eerst de index, dan 01, 02, … */
export function englishChapters(files: Record<string, string> = enFiles): GuideChapter[] {
  return Object.entries(files)
    .map(([path, markdown]) => ({ file: baseName(path), markdown }))
    .sort((a, b) => {
      if (a.file === "README.md") return -1;
      if (b.file === "README.md") return 1;
      return a.file.localeCompare(b.file);
    })
    .map((c) => ({ ...c, anchor: chapterAnchor(c.file) }));
}

export const dutchGuide: string = nlGuide;

/** De Engelse handleiding voor een UI-taal die met "en" begint; anders de Nederlandse. */
export function guideFor(language: string | undefined): "en" | "nl" {
  return (language ?? "").toLowerCase().startsWith("en") ? "en" : "nl";
}

export type GuideLink =
  /** Naar een hoofdstuk in deze Help. */
  | { kind: "chapter"; anchor: string }
  /** Binnen dezelfde pagina (`#kop`) of naar buiten (`https://…`): ongewijzigd. */
  | { kind: "keep" }
  /** Naar een document buiten de handleiding (`../extensions/…`): alleen tekst. */
  | { kind: "text" };

/** Wat er met een link in de Engelse handleiding gebeurt. */
export function resolveGuideLink(href: string | undefined): GuideLink {
  if (!href) return { kind: "text" };
  if (/^(https?:|mailto:|#)/i.test(href)) return { kind: "keep" };
  const m = /^(?:\.\/)?([^/#]+\.md)(?:#.*)?$/.exec(href);
  if (m?.[1]) return { kind: "chapter", anchor: chapterAnchor(m[1]) };
  return { kind: "text" };
}

/** De URL van een afbeelding uit de handleiding, of `null` als die (nog) niet bestaat. */
export function guideImageUrl(
  src: string | undefined,
  images: Record<string, string> = enImages,
): string | null {
  if (!src || /^(https?:|data:)/i.test(src)) return src ?? null;
  const name = baseName(src);
  const hit = Object.entries(images).find(([path]) => baseName(path) === name);
  return hit ? hit[1] : null;
}
